import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import type { NotificationSettings } from '../types';

const defaultSettings: Omit<NotificationSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  // Partner Events
  notify_partner_connected: true,
  notify_partner_removed: true,

  // Namen-Swipen
  notify_name_match: true,
  notify_partner_favorited_name: false,

  // Checklisten
  notify_checklist_item_completed: true,
  notify_checklist_note_added: false,
  notify_checklist_created: true,

  // Kalender
  notify_appointment_created: true,
  notify_appointment_reminder: true,
  notify_appointment_updated: false,

  // Erinnerungs-Zeitpunkte
  reminder_1_hour: true,
  reminder_1_day: true,
  reminder_1_week: true,

  // Schwangerschaft
  notify_new_ssw: true,
  notify_trimester_change: true,

  // Wehenzähler (globale Einstellung - für Kompatibilität beibehalten)
  notify_contraction_timer: true,
  // Wehenzähler (detaillierte Einstellungen)
  notify_contraction_tracking_started: true,
  notify_contraction_stats_update: true,
  notify_contraction_emergency_event: true,

  // Schlafplan
  notify_sleep_plan: true,
  notify_sleep_plan_early_warning: true,
  notify_sleep_plan_window_opens: true,
  notify_sleep_plan_window_closes: true,
  notify_sleep_plan_overtired: true,

  // Medikamenten-Plan
  notify_medication_reminder: true,

  // System
  notify_welcome: true,
  notify_premium_activated: true,

  // Push
  push_enabled: true,
};

export function useNotificationSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Settings laden
  const fetchSettings = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setSettings(data);
      } else {
        // Falls keine Settings existieren, erstelle sie mit Defaults
        const { data: newSettings, error: insertError } = await supabase
          .from('notification_settings')
          .insert({ user_id: user.id, ...defaultSettings })
          .select()
          .single();

        if (insertError) throw insertError;
        setSettings(newSettings);
      }
    } catch (err) {
      console.error('Error fetching notification settings:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Einzelne Setting aktualisieren
  const updateSetting = useCallback(
    async (
      key: keyof Omit<NotificationSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
      value: boolean
    ) => {
      if (!user?.id || !settings) return false;

      // Optimistisches Update
      const previousSettings = settings;
      setSettings({ ...settings, [key]: value });

      try {
        const { error: updateError } = await supabase
          .from('notification_settings')
          .update({ [key]: value, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);

        if (updateError) throw updateError;

        return true;
      } catch (err) {
        console.error('Error updating notification setting:', err);
        // Rollback bei Fehler
        setSettings(previousSettings);
        setError('Fehler beim Speichern der Einstellung');
        return false;
      }
    },
    [user?.id, settings]
  );

  // Alle Settings auf einmal aktualisieren
  const updateAllSettings = useCallback(
    async (
      newSettings: Partial<
        Omit<NotificationSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>
      >
    ) => {
      if (!user?.id || !settings) return false;

      setIsSaving(true);
      const previousSettings = settings;

      // Optimistisches Update
      setSettings({ ...settings, ...newSettings });

      try {
        const { error: updateError } = await supabase
          .from('notification_settings')
          .update({ ...newSettings, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);

        if (updateError) throw updateError;

        return true;
      } catch (err) {
        console.error('Error updating notification settings:', err);
        // Rollback bei Fehler
        setSettings(previousSettings);
        setError('Fehler beim Speichern der Einstellungen');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [user?.id, settings]
  );

  // Alle auf Default zurücksetzen
  const resetToDefaults = useCallback(async () => {
    if (!user?.id) return false;

    setIsSaving(true);

    try {
      const { error: updateError } = await supabase
        .from('notification_settings')
        .update({ ...defaultSettings, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setSettings((prev) =>
        prev ? { ...prev, ...defaultSettings } : null
      );

      return true;
    } catch (err) {
      console.error('Error resetting notification settings:', err);
      setError('Fehler beim Zurücksetzen der Einstellungen');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    isSaving,
    error,
    refetch: fetchSettings,
    updateSetting,
    updateAllSettings,
    resetToDefaults,
  };
}
